import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styles from './FishingMinigame.module.css';
import { ItemData } from '../data/ItemData';
import { gameConfigData } from '../data/data';
import { getItemImageSrc } from '../utils/getItemImageSrc';
import StatusDisplay from './Layout/StatusDisplay';
import { 
  getFishingTier, 
  getAllFishingBonuses, 
  selectFish, 
  getFishDifficultyMultipliers,
  calculateFishingWaitTime,
  shouldDoubleCatch,
  calculateFishValue,
  calculateFishingXPGain,
  getAvailableFish
} from '../utils/fishingUtils';

const FISHING_PHASES = {
  IDLE: 'idle',
  CASTING: 'casting',
  WAITING: 'waiting',
  HOOKING: 'hooking',
  REELING: 'reeling',
  RESULT: 'result',
};

const FishingMinigame = ({ playerFishingSkill, playerData, onGameEnd, onUseItem, onUpdatePlayerData }) => {
  const [gamePhase, setGamePhase] = useState(FISHING_PHASES.IDLE);
  const [message, setMessage] = useState('Select your equipment and cast your line to start fishing!');
  const [fishToCatch, setFishToCatch] = useState(null);
  
  // Equipment state
  const [selectedRod, setSelectedRod] = useState(null);
  const [attachedItem, setAttachedItem] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  
  // Consumables state
  const [selectedConsumable, setSelectedConsumable] = useState(null);
  const [consumableItemDetails, setConsumableItemDetails] = useState(null);
  
  // Minigame state
  const [playerBarY, setPlayerBarY] = useState(50); 
  const [playerBarVelocityY, setPlayerBarVelocityY] = useState(0);
  const [isPlayerActionActive, setIsPlayerActionActive] = useState(false); 
  const [fishY, setFishY] = useState(50); 
  const [fishTargetY, setFishTargetY] = useState(50);
  const [catchProgress, setCatchProgress] = useState(0); 

  const reactionTimerIdRef = useRef(null);
  const gameLoopRef = useRef(null);
  const lastFrameTimeRef = useRef(performance.now());

  // Create refs for current state values to avoid stale closures
  const gamePhaseRef = useRef(gamePhase);
  const fishToCatchRef = useRef(fishToCatch);
  const isPlayerActionActiveRef = useRef(isPlayerActionActive);
  const playerBarYRef = useRef(50);
  const playerBarVelocityYRef = useRef(0);
  const fishYRef = useRef(50);
  const fishTargetYRef = useRef(50);
  const catchProgressRef = useRef(0);

  // Update refs when state changes
  useEffect(() => { gamePhaseRef.current = gamePhase; }, [gamePhase]);
  useEffect(() => { fishToCatchRef.current = fishToCatch; }, [fishToCatch]);
  useEffect(() => { isPlayerActionActiveRef.current = isPlayerActionActive; }, [isPlayerActionActive]);
  useEffect(() => { playerBarYRef.current = playerBarY; }, [playerBarY]);
  useEffect(() => { playerBarVelocityYRef.current = playerBarVelocityY; }, [playerBarVelocityY]);
  useEffect(() => { fishYRef.current = fishY; }, [fishY]);
  useEffect(() => { fishTargetYRef.current = fishTargetY; }, [fishTargetY]);
  useEffect(() => { catchProgressRef.current = catchProgress; }, [catchProgress]);

  const gameSettings = useMemo(() => ({
    ...(gameConfigData.fishingMiniGame || {}),
    hookTimeWindow: gameConfigData.fishingMiniGame?.hookTimeWindow || 1500,
    baseBarHeight: gameConfigData.fishingMiniGame?.baseBarHeight || 150, 
    lureHeightRatio: gameConfigData.fishingMiniGame?.lureHeightRatio || 0.20, 
    fishIconHeightRatio: gameConfigData.fishingMiniGame?.fishIconHeightRatio || 0.15, 
    playerUpwardAcceleration: gameConfigData.fishingMiniGame?.playerUpwardAcceleration || 150, 
    playerGravity: gameConfigData.fishingMiniGame?.playerGravity || 200,          
    playerMaxUpwardSpeed: gameConfigData.fishingMiniGame?.playerMaxUpwardSpeed || 80,    
    playerMaxDownwardSpeed: gameConfigData.fishingMiniGame?.playerMaxDownwardSpeed || 100,   
    fishBaseSpeed: gameConfigData.fishingMiniGame?.fishBaseSpeed || 30, 
    fishDirectionChangeInterval: gameConfigData.fishingMiniGame?.fishDirectionChangeInterval || 1.5, 
    catchIncreaseRate: gameConfigData.fishingMiniGame?.catchIncreaseRate || 20, 
    catchDecreaseRate: gameConfigData.fishingMiniGame?.catchDecreaseRate || 15, 
    maxProgress: gameConfigData.fishingMiniGame?.maxProgress || 100,
    initialCatchProgress: gameConfigData.fishingMiniGame?.initialCatchProgress || 30,
    maxWaitTime: gameConfigData.fishingMiniGame?.maxWaitTime || 8000,    
    minWaitTime: gameConfigData.fishingMiniGame?.minWaitTime || 1000,    
  }), []);
  
  const playerBarDisplayHeight = gameSettings.baseBarHeight * gameSettings.lureHeightRatio;
  const fishIconDisplayHeight = gameSettings.baseBarHeight * gameSettings.fishIconHeightRatio;

  // Get all fishing equipment from inventory
  const fishingRods = useMemo(() => {
    return Object.entries(playerData.inventory)
      .filter(([itemId, item]) => {
        if (typeof item === 'object' && item !== null && item.key) {
          const itemData = ItemData[item.key];
          return itemData && itemData.toolType === 'fishingRod';
        }
        return false;
      })
      .map(([itemId, item]) => ({ ...item, id: itemId }));
  }, [playerData.inventory]);

  const fishingAttachments = useMemo(() => {
    return Object.entries(playerData.inventory)
      .filter(([itemId, item]) => {
        if (typeof item === 'object' && item !== null && item.key) {
          const itemData = ItemData[item.key];
          return itemData && itemData.type === 'fishingAttachment';
        } else if (typeof item === 'number' && item > 0) {
          const itemData = ItemData[itemId];
          return itemData && itemData.type === 'fishingAttachment';
        }
        return false;
      })
      .map(([itemId, item]) => {
        if (typeof item === 'object') {
          return { ...item, id: itemId, quantity: 1 };
        } else {
          return { ...ItemData[itemId], id: itemId, quantity: item, key: itemId };
        }
      });
  }, [playerData.inventory]);

  // Get consumable items from inventory
  const consumableItems = useMemo(() => {
    return Object.entries(playerData.inventory)
      .filter(([itemId, item]) => {
        if (typeof item === 'object' && item !== null && item.key) {
          const itemData = ItemData[item.key];
          return itemData && itemData.type === 'consumable';
        } else if (typeof item === 'number' && item > 0) {
          const itemData = ItemData[itemId];
          return itemData && itemData.type === 'consumable';
        }
        return false;
      })
      .map(([itemId, item]) => {
        if (typeof item === 'object') {
          return { ...item, id: itemId, quantity: 1 };
        } else {
          return { ...ItemData[itemId], id: itemId, quantity: item, key: itemId };
        }
      });
  }, [playerData.inventory]);

  // Get fishing tier and bonuses based on selected equipment
  const fishingTier = useMemo(() => {
    if (!selectedRod) return 0;
    const rodData = ItemData[selectedRod.key];
    return rodData?.fishingTier || 0;
  }, [selectedRod]);

  const fishingBonuses = useMemo(() => {
    let bonuses = getAllFishingBonuses(playerData);
    
    // Add attachment bonuses
    if (attachedItem) {
      const attachmentData = ItemData[attachedItem.key];
      if (attachmentData?.fishingBonuses) {
        Object.keys(attachmentData.fishingBonuses).forEach(bonusType => {
          if (typeof bonuses[bonusType] === 'boolean') {
            bonuses[bonusType] = bonuses[bonusType] || attachmentData.fishingBonuses[bonusType];
          } else {
            bonuses[bonusType] = (bonuses[bonusType] || 0) + attachmentData.fishingBonuses[bonusType];
          }
        });
      }
    }
    
    return bonuses;
  }, [playerData, attachedItem]);

  // Get available fish for current setup
  const availableFish = useMemo(() => {
    return getAvailableFish(fishingTier, fishingBonuses);
  }, [fishingTier, fishingBonuses]);

  // Initialize with equipped rod if available
  useEffect(() => {
    const equippedRodId = playerData.equipment.mainHand;
    if (equippedRodId && playerData.inventory[equippedRodId]) {
      const equippedRod = playerData.inventory[equippedRodId];
      const rodData = ItemData[equippedRod.key];
      if (rodData && rodData.toolType === 'fishingRod') {
        setSelectedRod({ ...equippedRod, id: equippedRodId });
      }
    }
  }, [playerData]);

  const resetReelingState = useCallback(() => {
    setPlayerBarY(50);
    setPlayerBarVelocityY(0);
    setFishY(50);
    setFishTargetY(50);
    setCatchProgress(gameSettings.initialCatchProgress);
    lastFrameTimeRef.current = performance.now();
  }, [gameSettings.initialCatchProgress]);

  const resetGameState = useCallback(() => {
    setGamePhase(FISHING_PHASES.IDLE);
    setMessage('Select your equipment and cast your line to start fishing!');
    setFishToCatch(null);
    resetReelingState();
    if (reactionTimerIdRef.current) {
      clearTimeout(reactionTimerIdRef.current);
      reactionTimerIdRef.current = null;
    }
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  }, [resetReelingState]);

  const handleAttachEquipment = useCallback(() => {
    if (!selectedAttachment) return;
    
    // If item has durability, create a copy with proper durability
    let itemToAttach = { ...selectedAttachment };
    const attachmentData = ItemData[selectedAttachment.key];
    
    if (attachmentData?.maxDurability) {
      itemToAttach.durability = attachmentData.maxDurability;
      itemToAttach.maxDurability = attachmentData.maxDurability;
    }
    
    setAttachedItem(itemToAttach);
    setSelectedAttachment(null);
    setMessage(`Attached ${attachmentData.name} to your fishing rod.`);
  }, [selectedAttachment]);

  const handleDetachEquipment = useCallback(() => {
    if (!attachedItem) return;
    
    setAttachedItem(null);
    setMessage('Attachment removed from your fishing rod.');
  }, [attachedItem]);

  const handleItemClick = useCallback((item, type) => {
    if (type === 'rod') {
      setSelectedRod(item);
      setSelectedItemDetails(ItemData[item.key]);
    } else if (type === 'attachment') {
      setSelectedAttachment(item);
      setSelectedItemDetails(ItemData[item.key]);
    }
  }, []);

  const handleItemHover = useCallback((item) => {
    if (item) {
      setSelectedItemDetails(ItemData[item.key]);
    }
  }, []);

  const handleConsumableClick = useCallback((item) => {
    setSelectedConsumable(item);
    setConsumableItemDetails(ItemData[item.key]);
  }, []);

  const handleConsumableHover = useCallback((item) => {
    if (item) {
      setConsumableItemDetails(ItemData[item.key]);
    }
  }, []);

  const handleUseConsumable = useCallback(() => {
    if (!selectedConsumable) return;
    
    onUseItem(selectedConsumable.id);
    setSelectedConsumable(null);
    setConsumableItemDetails(null);
  }, [selectedConsumable, onUseItem]);

  const startFishing = useCallback(() => {
    if (!selectedRod) {
      setMessage('Please select a fishing rod first.');
      return;
    }
    
    if (playerData.energy.current < 5) {
      setMessage('Not enough energy to start fishing.');
      return;
    }
    
    // Deduct energy
    const newPlayerData = {
      ...playerData,
      energy: {
        ...playerData.energy,
        current: playerData.energy.current - 5
      }
    };
    onUpdatePlayerData(newPlayerData);
    
    // Start fishing sequence
    setGamePhase(FISHING_PHASES.CASTING);
    setMessage('Casting your line...');
    
    // Simulate casting time
    setTimeout(() => {
      setGamePhase(FISHING_PHASES.WAITING);
      setMessage('Waiting for a bite...');
      
      // Calculate wait time with bonuses
      const baseWaitTime = Math.random() * (gameSettings.maxWaitTime - gameSettings.minWaitTime) + gameSettings.minWaitTime;
      const waitTime = calculateFishingWaitTime(baseWaitTime, fishingBonuses);
      
      // Start waiting for bite
      reactionTimerIdRef.current = setTimeout(() => {
        const selectedFish = selectFish(fishingTier, fishingBonuses, playerFishingSkill);
        
        if (selectedFish) {
          setFishToCatch(selectedFish);
          setGamePhase(FISHING_PHASES.HOOKING);
          setMessage(`🎣 A ${selectedFish.name} is biting! Click "Hook it!" quickly!`);
          
          // Auto-fail if not hooked in time
          reactionTimerIdRef.current = setTimeout(() => {
            if (gamePhaseRef.current === FISHING_PHASES.HOOKING) {
              setGamePhase(FISHING_PHASES.RESULT);
              setMessage('The fish got away! You were too slow to hook it.');
            }
          }, gameSettings.hookTimeWindow);
        } else {
          setGamePhase(FISHING_PHASES.RESULT);
          setMessage('Nothing bit this time. Try again!');
        }
      }, waitTime);
    }, 1000);
  }, [selectedRod, playerData, onUpdatePlayerData, fishingTier, fishingBonuses, playerFishingSkill, gameSettings]);

  const handleSuccessfulCatch = useCallback(() => {
    if (!fishToCatch) return;
    
    setGamePhase(FISHING_PHASES.RESULT);
    
    // Calculate rewards
    const fishValue = calculateFishValue(fishToCatch, fishingBonuses);
    const xpGain = calculateFishingXPGain(fishToCatch.tier * 5 + 10, fishingBonuses);
    const doubleCatch = shouldDoubleCatch(fishingBonuses);
    const finalQuantity = doubleCatch ? 2 : 1;
    
    // Update player data
    const newPlayerData = { ...playerData };
    
    // Add fish to inventory
    const fishKey = fishToCatch.key;
    if (newPlayerData.inventory[fishKey]) {
      if (typeof newPlayerData.inventory[fishKey] === 'number') {
        newPlayerData.inventory[fishKey] += finalQuantity;
      } else {
        newPlayerData.inventory[fishKey].quantity = (newPlayerData.inventory[fishKey].quantity || 1) + finalQuantity;
      }
    } else {
      newPlayerData.inventory[fishKey] = finalQuantity;
    }
    
    // Add XP
    newPlayerData.skills.fishing.xp += xpGain;
    
    // Add gold
    newPlayerData.gold += fishValue;
    
    // Reduce attachment durability
    if (attachedItem && attachedItem.durability !== undefined) {
      const updatedAttachment = { ...attachedItem };
      updatedAttachment.durability -= 1;
      
      if (updatedAttachment.durability <= 0) {
        setAttachedItem(null);
        setMessage(`Caught ${finalQuantity}x ${fishToCatch.name}! +${xpGain} XP, +${fishValue} gold. Your attachment broke!`);
      } else {
        setAttachedItem(updatedAttachment);
        setMessage(`Caught ${finalQuantity}x ${fishToCatch.name}! +${xpGain} XP, +${fishValue} gold.${doubleCatch ? ' Double catch!' : ''}`);
      }
    } else {
      setMessage(`Caught ${finalQuantity}x ${fishToCatch.name}! +${xpGain} XP, +${fishValue} gold.${doubleCatch ? ' Double catch!' : ''}`);
    }
    
    onUpdatePlayerData(newPlayerData);
  }, [fishToCatch, fishingBonuses, playerData, onUpdatePlayerData, attachedItem]);

  const attemptHook = useCallback(() => {
    if (gamePhase !== FISHING_PHASES.HOOKING || !fishToCatch) return;
    
    // Clear the timeout
    if (reactionTimerIdRef.current) {
      clearTimeout(reactionTimerIdRef.current);
      reactionTimerIdRef.current = null;
    }
    
    // Start reeling phase
    setGamePhase(FISHING_PHASES.REELING);
    setMessage('Hooked! Keep the bobber over the fish to reel it in!');
    resetReelingState();
    
    // Start game loop
    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [gamePhase, fishToCatch, resetReelingState]);

  const gameLoop = useCallback((currentTime) => {
    if (gamePhaseRef.current !== FISHING_PHASES.REELING) return;
    
    const deltaTime = (currentTime - lastFrameTimeRef.current) / 1000;
    lastFrameTimeRef.current = currentTime;
    
    const fishMultipliers = getFishDifficultyMultipliers(fishToCatchRef.current, fishingBonuses);
    
    // Update player bar physics
    const isActive = isPlayerActionActiveRef.current;
    let newVelocityY = playerBarVelocityYRef.current;
    
    if (isActive) {
      newVelocityY -= gameSettings.playerUpwardAcceleration * deltaTime;
    } else {
      newVelocityY += gameSettings.playerGravity * deltaTime;
    }
    
    newVelocityY = Math.max(-gameSettings.playerMaxUpwardSpeed, Math.min(gameSettings.playerMaxDownwardSpeed, newVelocityY));
    
    let newPlayerBarY = playerBarYRef.current + newVelocityY * deltaTime;
    newPlayerBarY = Math.max(0, Math.min(100, newPlayerBarY));
    
    setPlayerBarVelocityY(newVelocityY);
    setPlayerBarY(newPlayerBarY);
    
    // Update fish position
    const timeSinceLastDirectionChange = (currentTime - (fishToCatchRef.current?.lastDirectionChange || 0));
    const shouldChangeDirection = timeSinceLastDirectionChange > (fishMultipliers.directionChangeInterval * 1000);
    
    if (shouldChangeDirection) {
      const newTargetY = Math.random() * 100;
      setFishTargetY(newTargetY);
      setFishToCatch(prev => ({ ...prev, lastDirectionChange: currentTime }));
    }
    
    // Move fish towards target
    const fishSpeed = gameSettings.fishBaseSpeed * fishMultipliers.speed;
    const fishDiff = fishTargetYRef.current - fishYRef.current;
    const fishMovement = Math.sign(fishDiff) * Math.min(Math.abs(fishDiff), fishSpeed * deltaTime);
    const newFishY = fishYRef.current + fishMovement;
    
    setFishY(newFishY);
    
    // Check overlap and update progress
    const overlap = Math.abs(newPlayerBarY - newFishY) < 15;
    let newProgress = catchProgressRef.current;
    
    if (overlap) {
      newProgress += gameSettings.catchIncreaseRate * deltaTime;
    } else {
      newProgress -= gameSettings.catchDecreaseRate * deltaTime;
    }
    
    newProgress = Math.max(0, Math.min(gameSettings.maxProgress, newProgress));
    setCatchProgress(newProgress);
    
    // Check win/loss conditions
    if (newProgress >= gameSettings.maxProgress) {
      handleSuccessfulCatch();
      return;
    }
    
    if (newProgress <= 0) {
      setGamePhase(FISHING_PHASES.RESULT);
      setMessage('The fish escaped! Try again.');
      return;
    }
    
    // Continue loop
    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [fishingBonuses, gameSettings, handleSuccessfulCatch]);

  const handlePlayerClick = useCallback((e) => {
    e.preventDefault();
    if (gamePhase === FISHING_PHASES.REELING) {
      setIsPlayerActionActive(true);
      setTimeout(() => setIsPlayerActionActive(false), 100);
    }
  }, [gamePhase]);

  const renderLeftPanel = () => (
    <div className={styles.leftPanel}>
      <div className={styles.playerStats}>
        <h3>Player Stats</h3>
        <StatusDisplay
          health={playerData.health}
          mana={playerData.mana}
          energy={playerData.energy}
        />
        
        <div className={styles.fishingSkill}>
          <h4>Fishing Skill</h4>
          <div className={styles.skillDisplay}>
            <div className={styles.skillLevel}>Level {playerFishingSkill}</div>
            <div className={styles.skillXP}>XP: {playerData.skills.fishing.xp}</div>
          </div>
        </div>

        {Object.keys(fishingBonuses).some(key => fishingBonuses[key] > 0) && (
          <div className={styles.bonusesDisplay}>
            <h4>Active Bonuses</h4>
            {Object.entries(fishingBonuses).map(([bonus, value]) => {
              if (value > 0) {
                return (
                  <div key={bonus} className={styles.bonusItem}>
                    {bonus}: +{typeof value === 'number' ? (value * 100).toFixed(0) + '%' : value}
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>

      <div className={styles.descriptionPanel}>
        <h3>Details</h3>
        {selectedItemDetails ? (
          <div className={styles.itemDetails}>
            <div className={styles.detailsHeader}>
              <img src={getItemImageSrc(selectedItemDetails.img)} alt={selectedItemDetails.name} />
              <div>
                <h4>{selectedItemDetails.name}</h4>
                <div className={styles.itemType}>{selectedItemDetails.type}</div>
              </div>
            </div>
            
            <div className={styles.detailsContent}>
              <p>{selectedItemDetails.description}</p>
              
              {selectedItemDetails.fishingTier !== undefined && (
                <div className={styles.detailRow}>
                  <strong>Fishing Tier:</strong> {selectedItemDetails.fishingTier}
                </div>
              )}
              
              {selectedItemDetails.fishingBonuses && (
                <div className={styles.detailSection}>
                  <strong>Fishing Bonuses:</strong>
                  <ul>
                    {Object.entries(selectedItemDetails.fishingBonuses).map(([bonus, value]) => (
                      <li key={bonus}>
                        {bonus}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : `+${(value * 100).toFixed(0)}%`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedItemDetails.maxDurability && (
                <div className={styles.detailRow}>
                  <strong>Durability:</strong> {selectedItemDetails.maxDurability}
                </div>
              )}
              
              <div className={styles.detailRow}>
                <strong>Value:</strong> {selectedItemDetails.goldValue} gold
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.noDetailsMessage}>
            Select an item to view details
          </div>
        )}
      </div>
    </div>
  );

  const renderCenterPanel = () => (
    <div className={styles.centerPanel}>
      <div className={styles.gameArea}>
        <div className={styles.messageArea}>
          <p>{message}</p>
        </div>
        
        {gamePhase === FISHING_PHASES.REELING && fishToCatch && (
          <div 
            className={`${styles.reelingInteractArea} ${isPlayerActionActive ? styles.activeControl : ''}`}
            onClick={handlePlayerClick}
            onTouchStart={handlePlayerClick}
            tabIndex={-1} 
          >
            <div className={styles.instructionText}>
              🎣 CLICK to make the bobber float up - Keep it over the fish!
            </div>
            <div className={styles.mainTrack} style={{ height: `${gameSettings.baseBarHeight}px` }}>
              <div 
                className={`${styles.playerBar} ${isPlayerActionActive ? styles.activePlayerBar : ''}`} 
                style={{ 
                  top: `${Math.max(0, Math.min(gameSettings.baseBarHeight - playerBarDisplayHeight, (playerBarY / 100) * gameSettings.baseBarHeight - (playerBarDisplayHeight / 2)))}px`, 
                  height: `${playerBarDisplayHeight}px` 
                }}
              ></div>
              <div 
                className={styles.fishIcon} 
                style={{ 
                  top: `${Math.max(0, Math.min(gameSettings.baseBarHeight - fishIconDisplayHeight, (fishY / 100) * gameSettings.baseBarHeight - (fishIconDisplayHeight / 2)))}px`, 
                  height: `${fishIconDisplayHeight}px`, 
                  backgroundImage: `url(${getItemImageSrc(fishToCatch.key)})` 
                }}
              ></div>
            </div>
            <div className={styles.catchProgressContainer}>
              <div className={styles.catchProgressBar} style={{ width: `${catchProgress}%` }}></div>
            </div>
          </div>
        )}
        
        {gamePhase === FISHING_PHASES.IDLE && (
          <div className={styles.idleArea}>
            <div className={styles.fishPreview}>
              <h4>Available Fish (Tier {fishingTier})</h4>
              <div className={styles.fishGrid}>
                {availableFish.slice(0, 8).map(fish => (
                  <div key={fish.key} className={styles.fishItem}>
                    <img src={getItemImageSrc(fish.key)} alt={fish.name} />
                    <div className={styles.fishName}>{fish.name}</div>
                    <div className={styles.fishDifficulty}>{fish.difficulty}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div className={styles.gameActions}>
          {gamePhase === FISHING_PHASES.IDLE && (
            <button 
              className={styles.castButton}
              onClick={startFishing}
              disabled={!selectedRod || playerData.energy.current < 5}
            >
              Cast (5 Energy)
            </button>
          )}
          
          {gamePhase === FISHING_PHASES.HOOKING && (
            <button onClick={attemptHook} className={styles.hookButton}>
              Hook it!
            </button>
          )}
          
          {gamePhase === FISHING_PHASES.RESULT && (
            <button onClick={resetGameState} className={styles.continueButton}>
              Continue Fishing
            </button>
          )}
          
          {(gamePhase === FISHING_PHASES.WAITING || gamePhase === FISHING_PHASES.REELING) && (
            <button onClick={resetGameState} className={styles.cancelFishingButton}>
              Cancel Cast
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderConsumablesPanel = () => (
    <div className={styles.consumablesPanel}>
      <div className={styles.consumablesHeader}>
        <h3>Consumables</h3>
      </div>
      
      <div className={styles.consumablesGrid}>
        {consumableItems.map(item => {
          const itemData = ItemData[item.key];
          const isSelected = selectedConsumable?.id === item.id;
          return (
            <div
              key={item.id}
              className={`${styles.consumableItem} ${isSelected ? styles.selectedConsumable : ''}`}
              onClick={() => handleConsumableClick(item)}
              onMouseEnter={() => handleConsumableHover(item)}
              title={itemData.name}
            >
              <img src={getItemImageSrc(itemData.img)} alt={itemData.name} />
              {item.quantity > 1 && (
                <div className={styles.consumableQuantity}>x{item.quantity}</div>
              )}
            </div>
          );
        })}
      </div>

      {consumableItemDetails && (
        <div className={styles.consumableDetails}>
          <div className={styles.consumableDetailsHeader}>
            <img src={getItemImageSrc(consumableItemDetails.img)} alt={consumableItemDetails.name} />
            <div>
              <h4>{consumableItemDetails.name}</h4>
              <div className={styles.consumableType}>{consumableItemDetails.type}</div>
            </div>
          </div>
          
          <div className={styles.consumableDetailsContent}>
            <p>{consumableItemDetails.description}</p>
            
            {consumableItemDetails.effects && (
              <div className={styles.effectsSection}>
                <strong>Effects:</strong>
                <ul>
                  {Object.entries(consumableItemDetails.effects).map(([effect, value]) => (
                    <li key={effect}>
                      {effect}: {typeof value === 'string' ? value : `+${value}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className={styles.consumableValue}>
              <strong>Value:</strong> {consumableItemDetails.goldValue} gold
            </div>
          </div>

          {selectedConsumable && (
            <button 
              className={styles.useConsumableButton}
              onClick={handleUseConsumable}
            >
              Use {consumableItemDetails.name}
            </button>
          )}
        </div>
      )}
      
      {consumableItems.length === 0 && (
        <div className={styles.noConsumables}>
          <p>No consumables available</p>
        </div>
      )}
    </div>
  );

  const renderRightPanel = () => (
    <div className={styles.rightPanel}>
      <div className={styles.rightPanelTabs}>
        <button 
          className={`${styles.tabButton} ${styles.activeTab}`}
          disabled
        >
          Equipment
        </button>
        <button 
          className={`${styles.tabButton} ${styles.activeTab}`}
          disabled
        >
          Items
        </button>
      </div>
      
      <div className={styles.rightPanelContent}>
        <div className={styles.equipmentSection}>
          <div className={styles.equipmentSelection}>
            <div className={styles.equipmentCategory}>
              <h3>Fishing Rods</h3>
              <div className={styles.equipmentGrid}>
                {fishingRods.map(rod => {
                  const rodData = ItemData[rod.key];
                  const isSelected = selectedRod?.id === rod.id;
                  return (
                    <div
                      key={rod.id}
                      className={`${styles.equipmentItem} ${isSelected ? styles.selectedItem : ''}`}
                      onClick={() => handleItemClick(rod, 'rod')}
                      onMouseEnter={() => handleItemHover(rod)}
                      title={rodData.name}
                    >
                      <img src={getItemImageSrc(rodData.img)} alt={rodData.name} />
                      {rod.durability !== undefined && (
                        <div className={styles.durabilityBar}>
                          <div 
                            className={styles.durabilityFill} 
                            style={{ width: `${(rod.durability / rod.maxDurability) * 100}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.equipmentCategory}>
              <h3>Attachments</h3>
              <div className={styles.equipmentGrid}>
                {fishingAttachments.map(attachment => {
                  const attachmentData = ItemData[attachment.key];
                  const isSelected = selectedAttachment?.id === attachment.id;
                  return (
                    <div
                      key={attachment.id}
                      className={`${styles.equipmentItem} ${isSelected ? styles.selectedItem : ''}`}
                      onClick={() => handleItemClick(attachment, 'attachment')}
                      onMouseEnter={() => handleItemHover(attachment)}
                      title={attachmentData.name}
                    >
                      <img src={getItemImageSrc(attachmentData.img)} alt={attachmentData.name} />
                      {attachment.quantity > 1 && (
                        <div className={styles.quantity}>x{attachment.quantity}</div>
                      )}
                      {attachment.durability !== undefined && (
                        <div className={styles.durabilityBar}>
                          <div 
                            className={styles.durabilityFill} 
                            style={{ width: `${(attachment.durability / attachment.maxDurability) * 100}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.equippedGear}>
              <div className={styles.gearSlot}>
                <div className={styles.slotLabel}>Equipped Rod</div>
                <div className={styles.slotContent}>
                  {selectedRod ? (
                    <div className={styles.equippedItem}>
                      <img src={getItemImageSrc(ItemData[selectedRod.key].img)} alt={ItemData[selectedRod.key].name} />
                      <div className={styles.equipmentName}>{ItemData[selectedRod.key].name}</div>
                    </div>
                  ) : (
                    <div className={styles.emptySlot}>No rod selected</div>
                  )}
                </div>
              </div>

              <div className={styles.gearSlot}>
                <div className={styles.slotLabel}>Attachment</div>
                <div className={styles.slotContent}>
                  {attachedItem ? (
                    <div className={styles.equippedItem}>
                      <img src={getItemImageSrc(ItemData[attachedItem.key].img)} alt={ItemData[attachedItem.key].name} />
                      <div className={styles.equipmentName}>{ItemData[attachedItem.key].name}</div>
                      {attachedItem.durability !== undefined && (
                        <div className={styles.durabilityInfo}>
                          {attachedItem.durability}/{attachedItem.maxDurability}
                        </div>
                      )}
                      <button className={styles.detachButton} onClick={handleDetachEquipment}>
                        Detach
                      </button>
                    </div>
                  ) : (
                    <div className={styles.emptySlot}>No attachment</div>
                  )}
                </div>
              </div>

              {selectedAttachment && !attachedItem && (
                <button 
                  className={styles.attachButton}
                  onClick={handleAttachEquipment}
                >
                  Attach {ItemData[selectedAttachment.key].name}
                </button>
              )}
            </div>
          </div>
        </div>

        {renderConsumablesPanel()}
      </div>
    </div>
  );

  useEffect(() => {
    return () => { 
      if (reactionTimerIdRef.current) {
        clearTimeout(reactionTimerIdRef.current);
        reactionTimerIdRef.current = null;
      }
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    };
  }, []);

  return (
    <div className={styles.fishingOverlay}>
      <div className={styles.fishingWindow}>
        <div className={styles.fishingHeader}>
          <h2>Fishing</h2>
          <button 
            className={styles.quitButton}
            onClick={() => onGameEnd({ outcome: 'quit' })}
          >
            Quit
          </button>
        </div>
        
        <div className={styles.fishingContent}>
          {renderLeftPanel()}
          {renderCenterPanel()}
          {renderRightPanel()}
        </div>
      </div>
    </div>
  );
};

export default FishingMinigame;